import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal, Table, message } from 'antd';
import squareCatalogApi from '../../../services/squareCatalogApi';
import squareErrorToast from './squareErrorToast';

// Company-wide review queue for sync-added hidden products. Rows are
// seeded from props each time the modal opens and removed optimistically
// on resolve; the parent refreshes its own counts through onChanged().
//
// Product links call onOpenProduct instead of navigating: this modal is
// rendered from inside the menu editor route (menus.js), so a same-route
// navigate() is a no-op for the router and the deep-link selection logic
// never re-arms. The parent owns opening the product in-page.
const NeedsReviewModal = ({ open, items, onClose, onChanged, onOpenProduct }) => {
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const [rows, setRows] = useState([]);
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    if (open) setRows(items || []);
  }, [open, items]);

  const resolve = async (productIds, action) => {
    const singleRow =
      productIds.length === 1
        ? rows.find((r) => r.productid === productIds[0])
        : null;
    try {
      setResolving(true);
      await squareCatalogApi.needsReviewResolve({
        companyid: globalCompany?.id,
        productids: productIds,
        action,
      });
      setRows((prev) => prev.filter((r) => !productIds.includes(r.productid)));
      onChanged();
      // Sync only manages the Square menus, so someone viewing a manual menu
      // would otherwise conclude "Show" did nothing. A single-row Show jumps
      // straight to the product (closing the queue) via the in-page deep
      // link; the toast names the menu it landed in. Rows from an older api
      // (no menu/category ids) just get the toast.
      if (action === 'show' && singleRow) {
        message.success(`Shown in ${singleRow.menuname}`, 4);
        if (onOpenProduct && singleRow.menuid && singleRow.categoryid) {
          onClose();
          onOpenProduct({
            menuid: singleRow.menuid,
            categoryid: singleRow.categoryid,
            productid: singleRow.productid,
          });
        }
      }
    } catch (err) {
      squareErrorToast(err);
      onClose();
    } finally {
      setResolving(false);
    }
  };

  const confirmBulk = (action) => {
    const ids = rows.map((r) => r.productid);
    Modal.confirm({
      title: action === 'show' ? 'Show all products?' : 'Keep all hidden?',
      content:
        action === 'show'
          ? `${ids.length} products will be made visible to customers.`
          : `${ids.length} products stay hidden and leave this list.`,
      onOk: () => resolve(ids, action),
    });
  };

  return (
    <Modal title="Needs review" open={open} onCancel={onClose} footer={null} width={860}>
      {rows.length === 0 ? (
        <p className="py-4 text-sm text-gray-600 dark:text-slate-300">
          All reviewed ✓
        </p>
      ) : (
        <>
          <div className="mb-3 flex justify-end gap-2">
            <button
              type="button"
              disabled={resolving}
              onClick={() => confirmBulk('show')}
              className="rounded-md border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-600"
            >
              Show all
            </button>
            <button
              type="button"
              disabled={resolving}
              onClick={() => confirmBulk('keepHidden')}
              className="rounded-md border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-600"
            >
              Keep all hidden
            </button>
          </div>
          <Table
            size="small"
            pagination={false}
            rowKey="productid"
            dataSource={rows}
            columns={[
              {
                title: 'Product',
                dataIndex: 'productname',
                render: (v, r) => (
                  <span>
                    {r.menuid && r.categoryid ? (
                      <button
                        type="button"
                        onClick={() => {
                          onClose();
                          onOpenProduct?.({
                            menuid: r.menuid,
                            categoryid: r.categoryid,
                            productid: r.productid,
                          });
                        }}
                        className="font-semibold text-blue-600 underline hover:text-blue-700 dark:text-blue-400"
                      >
                        {v}
                      </button>
                    ) : (
                      v
                    )}
                    {r.squarename && r.squarename !== v && (
                      <span className="ml-1 text-xs text-gray-400">
                        Square: {r.squarename}
                      </span>
                    )}
                  </span>
                ),
              },
              { title: 'Menu', dataIndex: 'menuname' },
              { title: 'Category', dataIndex: 'categoryname' },
              {
                title: 'Price',
                dataIndex: 'firstitemprice',
                render: (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`),
              },
              {
                title: '',
                dataIndex: 'productid',
                render: (id) => (
                  <span className="flex gap-2">
                    <button
                      type="button"
                      disabled={resolving}
                      onClick={() => resolve([id], 'show')}
                      className="rounded-md bg-blue-600 px-2 py-1 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      Show
                    </button>
                    <button
                      type="button"
                      disabled={resolving}
                      onClick={() => resolve([id], 'keepHidden')}
                      className="rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-600"
                    >
                      Keep hidden
                    </button>
                  </span>
                ),
              },
            ]}
          />
        </>
      )}
    </Modal>
  );
};

export default NeedsReviewModal;
